import { LayoutMap } from './types';

export const layoutTree = (
  peopleMap: Record<string, any>,
  id: string,
  startX: number,
  startY: number,
  spacingX: number,
  layout: LayoutMap,
  drawn = new Set<string>()
): number => {
  if (drawn.has(id)) return 0;
  drawn.add(id);

  const person = peopleMap[id];
  if (!person) return 0;

  const partnerId = person.spouses;
  const partner = partnerId ? peopleMap[partnerId] : null;
  const children = [...(person.children || []), ...(partner?.children || [])]
    .map((c: string) => peopleMap[c])
    .filter(Boolean);

  // אם אין ילדים, נשים את בני הזוג אחד ליד השני עם מרווח רחב
  if (!children.length) {
    layout[person.id] = { x: startX, y: startY };
    if (partnerId) layout[partnerId] = { x: startX + 160, y: startY }; // הגדלת המרווח לבני הזוג
    return partnerId ? 320 : 160;
  }

  let childX = startX;
  const childY = startY + 120;
  let totalWidth = 0;

  // נחשב את המרחק בין הילדים, תוך התחשבות אם יש להם בני זוג
  children.forEach((child: any) => {
    const width = layoutTree(peopleMap, child.id, childX, childY, spacingX, layout, drawn);
    const childPartner = child.spouses ? peopleMap[child.spouses] : null;

    // אם יש בן/בת זוג, נכפיל את המרחק
    const partnerWidth = childPartner ? 160 : 0;
    childX += width + spacingX + partnerWidth;
    totalWidth += width + spacingX + partnerWidth;
  });
  totalWidth -= spacingX;

  const midX = startX + totalWidth / 2 - 70;

  // מיקום האיש ובני הזוג (נשארים במרחק רחב יותר)
  layout[person.id] = { x: midX, y: startY };
  if (partner) layout[partner.id] = { x: midX + 160, y: startY }; // הגדלת המרווח בין בני הזוג

  return totalWidth;
};

export const drawTree = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutMap,
  offset: { x: number; y: number },
  scale: number,
  peopleMap: Record<string, any>,
  selectedId: string,
  manImage: HTMLImageElement,
  womanImage: HTMLImageElement
) => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.save();
  ctx.translate(offset.x, offset.y);
  ctx.scale(scale, scale);

  // Draw connections
  Object.entries(layout).forEach(([id, pos]) => {
    const person = peopleMap[id];
    if (!person) return;

    const partnerId = person.spouses;
    const partner = partnerId ? peopleMap[partnerId] : null;

    if (partner && layout[partner.id]) {
      const x1 = pos.x + 120;
      const y1 = pos.y + 20;
      const x2 = layout[partner.id].x;
      const y2 = layout[partner.id].y + 20;

      const cornerX = (x1 + x2) / 2;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(cornerX, y1);
      ctx.lineTo(cornerX, y2);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    const children = [...(person.children || []), ...(partner?.children || [])]
      .map((c: string) => peopleMap[c])
      .filter(Boolean);

    const midX = partner && layout[partner.id]
      ? (pos.x + layout[partner.id].x + 120) / 2
      : pos.x + 60;

    children.forEach((child: any) => {
      const childPos = layout[child.id];
      if (!childPos) return;

      const parentX = midX;
      const parentY = pos.y + 40;
      const verticalMidY = parentY + 20;
      const childX = childPos.x + 60;
      const childY = childPos.y;

      ctx.beginPath();
      ctx.moveTo(parentX, parentY - 20);
      ctx.lineTo(parentX, verticalMidY);
      ctx.lineTo(childX, verticalMidY);
      ctx.lineTo(childX, childY);
      ctx.strokeStyle = '#E2E0E0';
      ctx.stroke();
    });
  });

  // Draw nodes
  Object.entries(layout).forEach(([id, pos]) => {
    const person = peopleMap[id];
    if (!person) return;

    ctx.fillStyle =
      id === selectedId
        ? '#ffcc80'
        : person.gender === 'male'
        ? '#E0EEF9'
        : '#F7E8ED';

    ctx.fillRect(pos.x, pos.y, 120, 40);
    ctx.strokeRect(pos.x, pos.y, 120, 40);

    const imageSrc = person.image || (person.gender === 'male' ? manImage.src : womanImage.src);
    const img = new Image();
    img.src = imageSrc;

    const imgX = pos.x + 4;
    const imgY = pos.y + 2;
    const radius = 18;

    ctx.save();
    ctx.beginPath();
    ctx.arc(imgX + radius, imgY + radius, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, imgX, imgY, radius * 2, radius * 2);
    ctx.restore();

    ctx.fillStyle = '#000';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = '14px Arial';
    ctx.fillText(person.name, pos.x + 48, pos.y + 20);
  });

  ctx.restore();
};
